/**
 * Video Scroll
 * PC: sticky pin + vertical 3D slide transitions (scroll-driven).
 * Mobile: horizontal swipe carousel with nav, progress dots, and optional autoplay.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-video-scroll]';
  var MOBILE_QUERY = '(max-width: 768px)';
  var WHEEL_THRESHOLD = 8;
  var TOUCH_THRESHOLD = 48;
  var SWIPE_THRESHOLD = 50;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileViewport() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function VideoScroll(root) {
    this.root = root;
    this.track = root.querySelector('[data-video-scroll-track]');
    this.sticky = root.querySelector('[data-video-scroll-sticky]');
    this.viewport = root.querySelector('[data-video-scroll-viewport]');
    this.stage = root.querySelector('[data-video-scroll-stage]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-video-scroll-slide]'));
    this.prevBtn = root.querySelector('[data-video-scroll-prev]');
    this.nextBtn = root.querySelector('[data-video-scroll-next]');
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-video-scroll-dot]'));
    this.duration = parseInt(root.getAttribute('data-duration'), 10) || 520;
    this.index = 0;
    this.transitioning = false;
    this.mobileAnimating = false;
    this.mobileAnimTimer = null;
    this.loopEnabled = false;
    this.cloneFirst = null;
    this.cloneLast = null;
    this.wheelLockedUntil = 0;
    this.wheelCooldownMs = this.duration + 80;
    this.wheelAnimating = false;
    this.wheelAnimTimer = null;
    this.headerRetryTimers = [];
    this.touchStartY = 0;
    this.touchStartX = 0;
    this.touchDeltaX = 0;
    this.touchActive = false;
    this.isSwiping = false;
    this.pinned = false;
    this.lastHeaderOffset = -1;
    this.bound = {};
    this.resizeObserver = null;
    this.headerObserver = null;
    this.mobileMediaQuery = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
    this.autoplayTimer = null;
    this.autoplayResumeTimer = null;
    this.autoplayIntervalMs =
      (parseInt(root.getAttribute('data-mobile-autoplay-interval'), 10) || 0) * 1000;
    this.autoplayEnabled = this.autoplayIntervalMs > 0;

    if (!this.track || !this.sticky || !this.slides.length) return;

    if (prefersReducedMotion()) {
      this.root.classList.add('is-reduced-motion');
    }

    this.index = this.getActiveIndex();
    this.syncHeaderOffset(true);
    this.measureLayout();
    this.bind();
    this.syncVideos(this.index);
    this.scheduleHeaderOffsetRetries();

    if (this.shouldUseMobileCarousel()) {
      this.goToMobile(this.index, { instant: true, restartProgress: false, force: true });
      this.scheduleMobileLayoutRetries();
      this.syncAutoplay();
    } else {
      this.onScroll();
    }

    this.root.classList.add('is-vs-ready');
  }

  VideoScroll.prototype.shouldUseMobileCarousel = function () {
    return isMobileViewport() && this.slides.length >= 1;
  };

  VideoScroll.prototype.getActiveIndex = function () {
    for (var i = 0; i < this.slides.length; i += 1) {
      if (this.slides[i].classList.contains('is-active')) return i;
    }
    return 0;
  };

  VideoScroll.prototype.getStickyHeight = function () {
    return this.sticky ? this.sticky.offsetHeight : window.innerHeight;
  };

  VideoScroll.prototype.measureHeaderHeight = function () {
    var header = document.getElementById('shopify-section-header');
    var height = 0;
    if (header) {
      height = header.offsetHeight || 0;
      var announcement = document.getElementById('shopify-section-announcement');
      if (announcement) height += announcement.offsetHeight || 0;
    }

    if (height <= 0) {
      var storeHeader = document.querySelector('store-header.header, store-header, .header');
      if (storeHeader) height = storeHeader.getBoundingClientRect().height || 0;
    }

    if (height <= 0) {
      var fromVar = parseFloat(
        window.getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      if (!isNaN(fromVar) && fromVar > 0) height = fromVar;
    }

    return height > 0 ? height : 64;
  };

  VideoScroll.prototype.syncHeaderOffset = function (forceLayout) {
    var offset = this.measureHeaderHeight();
    this.root.style.setProperty('--vs-header-offset', offset + 'px');
    if (this.sticky && !this.shouldUseMobileCarousel()) {
      this.sticky.style.top = offset + 'px';
    }

    var changed = Math.abs(offset - this.lastHeaderOffset) > 0.5;
    this.lastHeaderOffset = offset;

    if ((changed || forceLayout) && !this.shouldUseMobileCarousel()) {
      this.updateTrackHeight();
      if (this.isSectionInPinRange()) {
        this.syncIndexFromScroll({ instant: true });
        this.scrollToIndex(this.index, 'auto');
      }
    }

    return offset;
  };

  VideoScroll.prototype.scheduleHeaderOffsetRetries = function () {
    var self = this;
    var delays = [0, 50, 150, 300, 800, 1500];

    delays.forEach(function (delay) {
      var timer = window.setTimeout(function () {
        self.syncHeaderOffset(false);
      }, delay);
      self.headerRetryTimers.push(timer);
    });

    if (document.readyState !== 'complete') {
      this.bound.onWindowLoad = function () {
        self.syncHeaderOffset(true);
      };
      window.addEventListener('load', this.bound.onWindowLoad);
    }
  };

  VideoScroll.prototype.getHeaderOffset = function () {
    if (this.lastHeaderOffset > 0) return this.lastHeaderOffset;

    if (this.root) {
      var fromRoot = parseFloat(
        window.getComputedStyle(this.root).getPropertyValue('--vs-header-offset')
      );
      if (!isNaN(fromRoot) && fromRoot > 0) return fromRoot;
    }

    if (this.sticky) {
      var fromSticky = parseFloat(window.getComputedStyle(this.sticky).top);
      if (!isNaN(fromSticky) && fromSticky > 0) return fromSticky;
    }

    return this.measureHeaderHeight();
  };

  VideoScroll.prototype.updateTrackHeight = function () {
    if (!this.track || !this.sticky) return;
    if (this.shouldUseMobileCarousel()) {
      this.track.style.height = '';
      return;
    }

    var count = Math.max(this.slides.length, 1);
    var stickyHeight = this.getStickyHeight();
    this.track.style.height = stickyHeight * count + 'px';
  };

  VideoScroll.prototype.getMobilePanelWidth = function () {
    if (!this.viewport) return 0;
    return this.viewport.getBoundingClientRect().width || this.viewport.clientWidth || 0;
  };

  VideoScroll.prototype.getMobileTrackSlides = function () {
    if (!this.stage) return this.slides.slice();
    return Array.prototype.slice.call(this.stage.children);
  };

  VideoScroll.prototype.prepareCloneSlide = function (source) {
    var clone = source.cloneNode(true);
    clone.classList.remove('is-active', 'is-incoming', 'is-entering', 'is-leaving');
    clone.setAttribute('data-video-scroll-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-index');

    var players = clone.querySelectorAll('[data-video-scroll-player]');
    for (var i = 0; i < players.length; i += 1) {
      players[i].removeAttribute('id');
      try {
        players[i].pause();
        players[i].currentTime = 0;
      } catch (e) {
        /* ignore */
      }
    }

    return clone;
  };

  VideoScroll.prototype.setupMobileLoop = function () {
    if (!this.stage || this.slides.length < 2) {
      this.teardownMobileLoop();
      return;
    }

    if (this.loopEnabled && this.cloneFirst && this.cloneLast) return;

    this.teardownMobileLoop();

    this.cloneLast = this.prepareCloneSlide(this.slides[this.slides.length - 1]);
    this.cloneFirst = this.prepareCloneSlide(this.slides[0]);
    this.stage.insertBefore(this.cloneLast, this.slides[0]);
    this.stage.appendChild(this.cloneFirst);
    this.loopEnabled = true;
  };

  VideoScroll.prototype.teardownMobileLoop = function () {
    if (this.cloneFirst && this.cloneFirst.parentNode) {
      this.cloneFirst.parentNode.removeChild(this.cloneFirst);
    }
    if (this.cloneLast && this.cloneLast.parentNode) {
      this.cloneLast.parentNode.removeChild(this.cloneLast);
    }
    this.cloneFirst = null;
    this.cloneLast = null;
    this.loopEnabled = false;
  };

  VideoScroll.prototype.getMobileTrackIndex = function (realIndex) {
    if (!this.loopEnabled) return realIndex;
    return realIndex + 1;
  };

  VideoScroll.prototype.setMobileSlideWidths = function () {
    if (!this.viewport || !this.stage) return false;
    var panelWidth = this.getMobilePanelWidth();
    if (panelWidth <= 0) return false;

    var trackSlides = this.getMobileTrackSlides();
    for (var i = 0; i < trackSlides.length; i += 1) {
      trackSlides[i].style.flex = '0 0 ' + panelWidth + 'px';
      trackSlides[i].style.width = panelWidth + 'px';
      trackSlides[i].style.maxWidth = panelWidth + 'px';
      trackSlides[i].style.minWidth = panelWidth + 'px';
    }

    this.stage.style.width = panelWidth * trackSlides.length + 'px';
    return true;
  };

  VideoScroll.prototype.getMobileSlideOffset = function (trackIndex) {
    return this.getMobilePanelWidth() * trackIndex;
  };

  VideoScroll.prototype.setMobileStageOffset = function (trackIndex) {
    if (!this.stage) return;
    var offset = this.getMobileSlideOffset(trackIndex);
    this.stage.style.transform = offset > 0 ? 'translate3d(-' + offset + 'px, 0, 0)' : 'translate3d(0, 0, 0)';
  };

  VideoScroll.prototype.clearMobileAnimLock = function () {
    this.mobileAnimating = false;
    if (this.mobileAnimTimer) {
      window.clearTimeout(this.mobileAnimTimer);
      this.mobileAnimTimer = null;
    }
    if (this.stage && this.bound.onMobileTransitionEnd) {
      this.stage.removeEventListener('transitionend', this.bound.onMobileTransitionEnd);
      this.bound.onMobileTransitionEnd = null;
    }
  };

  VideoScroll.prototype.snapMobileStage = function (trackIndex) {
    if (!this.stage) return;
    this.stage.style.transition = 'none';
    this.setMobileStageOffset(trackIndex);
    void this.stage.offsetWidth;
    this.stage.style.transition = '';
  };

  VideoScroll.prototype.measureLayout = function () {
    if (this.shouldUseMobileCarousel()) {
      this.track.style.height = '';
      this.setupMobileLoop();
      if (this.stage) {
        this.stage.style.transform = '';
        this.stage.style.width = '';
      }
      this.setMobileSlideWidths();
      this.setMobileStageOffset(this.getMobileTrackIndex(this.index));
      this.stopAutoplay();
      return;
    }

    this.teardownMobileLoop();
    this.clearMobileSlideStyles();
    if (this.stage) {
      this.stage.style.transform = '';
      this.stage.style.width = '';
    }
    this.updateTrackHeight();
    this.stopAutoplay();
  };

  VideoScroll.prototype.scheduleMobileLayoutRetries = function () {
    var self = this;
    var delays = [0, 50, 150, 300, 800];

    delays.forEach(function (delay) {
      var timer = window.setTimeout(function () {
        if (!self.shouldUseMobileCarousel() || self.mobileAnimating) return;
        self.setupMobileLoop();
        if (self.setMobileSlideWidths()) {
          self.setMobileStageOffset(self.getMobileTrackIndex(self.index));
        }
      }, delay);
      self.headerRetryTimers.push(timer);
    });
  };

  VideoScroll.prototype.clearMobileSlideStyles = function () {
    var trackSlides = this.getMobileTrackSlides();
    for (var i = 0; i < trackSlides.length; i += 1) {
      trackSlides[i].style.flex = '';
      trackSlides[i].style.width = '';
      trackSlides[i].style.maxWidth = '';
      trackSlides[i].style.minWidth = '';
    }
  };

  VideoScroll.prototype.getIndexFromScroll = function () {
    if (!this.track || !this.slides.length) return 0;
    var pageY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var rect = this.track.getBoundingClientRect();
    var trackTop = rect.top + pageY;
    var stickyHeight = this.getStickyHeight() || 1;
    var headerOffset = this.getHeaderOffset();
    var progress = (pageY - (trackTop - headerOffset)) / stickyHeight;
    var target = Math.round(progress);
    return Math.max(0, Math.min(this.slides.length - 1, target));
  };

  VideoScroll.prototype.syncIndexFromScroll = function (options) {
    if (!this.track || this.slides.length < 2 || this.shouldUseMobileCarousel()) return;
    var target = this.getIndexFromScroll();
    if (target === this.index) return;
    this.goTo(target, {
      instant: !!(options && options.instant),
      syncScroll: false
    });
  };

  VideoScroll.prototype.getPlayers = function (slide) {
    if (!slide) return [];
    return Array.prototype.slice.call(slide.querySelectorAll('[data-video-scroll-player]'));
  };

  VideoScroll.prototype.getActivePlayer = function (slide) {
    var players = this.getPlayers(slide);
    if (!players.length) return null;
    var mobile = isMobileViewport();
    var preferred = null;
    var single = null;
    var pcFallback = null;
    for (var i = 0; i < players.length; i += 1) {
      var device = players[i].getAttribute('data-video-device');
      if (device === 'single') single = players[i];
      if (device === 'pc') pcFallback = players[i];
      if (mobile && device === 'mobile') preferred = players[i];
      if (!mobile && device === 'pc') preferred = players[i];
    }
    if (mobile && !preferred) preferred = pcFallback;
    return preferred || single || players[0];
  };

  VideoScroll.prototype.pauseAll = function () {
    var all = this.root.querySelectorAll('[data-video-scroll-player]');
    for (var i = 0; i < all.length; i += 1) {
      try {
        all[i].pause();
      } catch (e) {
        /* ignore */
      }
    }
  };

  VideoScroll.prototype.playSlide = function (index) {
    var slide = this.slides[index];
    if (!slide) return;
    var player = this.getActivePlayer(slide);
    if (!player) return;

    player.muted = player.hasAttribute('muted');
    player.loop = player.hasAttribute('loop');
    player.playsInline = player.hasAttribute('playsinline');

    var tryPlay = function () {
      try {
        var playPromise = player.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
      } catch (e) {
        /* ignore autoplay rejection */
      }
    };

    if (player.readyState < 2) {
      var onReady = function () {
        tryPlay();
      };
      player.addEventListener('loadeddata', onReady, { once: true });
      player.addEventListener('canplay', onReady, { once: true });
      try {
        player.load();
      } catch (e) {
        tryPlay();
      }
    } else {
      tryPlay();
    }
  };

  VideoScroll.prototype.syncVideos = function (index) {
    this.pauseAll();
    this.playSlide(index);
  };

  VideoScroll.prototype.waitForTransition = function (el) {
    var fallback = this.duration + 120;
    return new Promise(function (resolve) {
      var done = false;
      var timer = window.setTimeout(finish, fallback);

      function finish() {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        window.clearTimeout(timer);
        resolve();
      }

      function onEnd(event) {
        if (event.target !== el) return;
        if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
          finish();
        }
      }

      el.addEventListener('transitionend', onEnd);
    });
  };

  VideoScroll.prototype.waitForCrossfade = function (leaving, entering) {
    var waits = [this.waitForTransition(entering)];
    if (leaving && leaving !== entering) {
      waits.push(this.waitForTransition(leaving));
    }
    return Promise.all(waits);
  };

  VideoScroll.prototype.resetSlideState = function (slide) {
    if (!slide) return;
    slide.classList.remove('is-incoming', 'is-entering', 'is-leaving', 'is-active');
  };

  VideoScroll.prototype.resetAllSlideStates = function () {
    for (var i = 0; i < this.slides.length; i += 1) {
      this.resetSlideState(this.slides[i]);
    }
  };

  VideoScroll.prototype.getScrollYForIndex = function (index) {
    if (!this.track) return 0;
    var rect = this.track.getBoundingClientRect();
    var pageY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var trackTop = rect.top + pageY;
    var stickyHeight = this.getStickyHeight();
    var headerOffset = this.getHeaderOffset();
    return Math.max(0, trackTop - headerOffset + stickyHeight * index);
  };

  VideoScroll.prototype.scrollToIndex = function (index, behavior) {
    if (this.shouldUseMobileCarousel()) return;

    var target = this.getScrollYForIndex(index);
    var pageY = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (Math.abs(pageY - target) < 1) return;

    var useSmooth =
      behavior === 'smooth' ||
      ((behavior == null || behavior === undefined) && !prefersReducedMotion());
    if (behavior === 'auto' || prefersReducedMotion()) useSmooth = false;

    var self = this;
    this.wheelAnimating = true;
    if (this.wheelAnimTimer) {
      window.clearTimeout(this.wheelAnimTimer);
      this.wheelAnimTimer = null;
    }

    window.scrollTo({
      top: target,
      behavior: useSmooth ? 'smooth' : 'auto'
    });

    this.wheelAnimTimer = window.setTimeout(function () {
      self.wheelAnimating = false;
      self.wheelAnimTimer = null;
    }, useSmooth ? this.wheelCooldownMs : 0);
  };

  VideoScroll.prototype.goToMobile = function (nextIndex, options) {
    options = options || {};
    var self = this;
    var max = this.slides.length - 1;
    var idx = nextIndex;
    var wrappingNext = false;
    var wrappingPrev = false;

    if (this.mobileAnimating && !options.instant && !options.force) return;

    this.setupMobileLoop();
    this.setMobileSlideWidths();

    if (options.loop) {
      if (idx > max) {
        wrappingNext = this.loopEnabled;
        idx = 0;
      } else if (idx < 0) {
        wrappingPrev = this.loopEnabled;
        idx = max;
      }
    } else {
      idx = Math.max(0, Math.min(max, idx));
    }

    if (idx === this.index && !options.force && !wrappingNext && !wrappingPrev) return;

    for (var i = 0; i < this.slides.length; i += 1) {
      this.slides[i].classList.toggle('is-active', i === idx);
    }

    var targetTrackIndex;
    if (wrappingNext) {
      targetTrackIndex = this.slides.length + 1;
    } else if (wrappingPrev) {
      targetTrackIndex = 0;
    } else {
      targetTrackIndex = this.getMobileTrackIndex(idx);
    }

    this.index = idx;
    this.updateMobileNav(options);

    var afterSettle = function () {
      self.clearMobileAnimLock();
      if (wrappingNext || wrappingPrev) {
        self.snapMobileStage(self.getMobileTrackIndex(self.index));
      }
      self.syncVideos(self.index);
    };

    if (options.instant || prefersReducedMotion()) {
      this.snapMobileStage(this.getMobileTrackIndex(this.index));
      this.syncVideos(this.index);
      this.clearMobileAnimLock();
      return;
    }

    this.mobileAnimating = true;
    this.setMobileStageOffset(targetTrackIndex);

    if (this.mobileAnimTimer) {
      window.clearTimeout(this.mobileAnimTimer);
      this.mobileAnimTimer = null;
    }

    var settled = false;
    var settle = function () {
      if (settled) return;
      settled = true;
      if (self.stage && self.bound.onMobileTransitionEnd) {
        self.stage.removeEventListener('transitionend', self.bound.onMobileTransitionEnd);
        self.bound.onMobileTransitionEnd = null;
      }
      afterSettle();
    };

    this.bound.onMobileTransitionEnd = function (event) {
      if (!self.stage || event.target !== self.stage) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      settle();
    };

    this.stage.addEventListener('transitionend', this.bound.onMobileTransitionEnd);
    this.mobileAnimTimer = window.setTimeout(settle, 420);
  };

  VideoScroll.prototype.restartDotProgress = function () {
    var activeDot = this.dots[this.index];
    if (!activeDot) return;

    activeDot.classList.remove('is-active');
    void activeDot.offsetWidth;
    activeDot.classList.add('is-active');
    activeDot.setAttribute('aria-current', 'true');
  };

  VideoScroll.prototype.syncAutoplayClass = function () {
    var isRunning =
      this.autoplayEnabled &&
      this.shouldUseMobileCarousel() &&
      !prefersReducedMotion() &&
      this.autoplayTimer !== null;

    this.root.classList.toggle('is-autoplay-active', isRunning);
  };

  VideoScroll.prototype.updateMobileNav = function (options) {
    options = options || {};

    for (var i = 0; i < this.dots.length; i += 1) {
      var isActive = i === this.index;
      this.dots[i].classList.toggle('is-active', isActive);
      this.dots[i].setAttribute('aria-current', isActive ? 'true' : 'false');
    }

    this.syncAutoplayClass();

    if (
      options.restartProgress !== false &&
      this.autoplayEnabled &&
      this.shouldUseMobileCarousel() &&
      !prefersReducedMotion() &&
      this.autoplayTimer
    ) {
      this.restartDotProgress();
    }
  };

  VideoScroll.prototype.startAutoplay = function () {
    var self = this;
    this.stopAutoplay();
    if (!this.autoplayEnabled || !this.shouldUseMobileCarousel() || prefersReducedMotion()) return;
    if (document.hidden || this.slides.length < 2) return;

    this.autoplayTimer = window.setTimeout(function () {
      self.autoplayTimer = null;
      self.syncAutoplayClass();
      self.goToMobile(self.index + 1, { loop: true, restartProgress: false });
      self.startAutoplay();
    }, this.autoplayIntervalMs);

    this.syncAutoplayClass();
    this.restartDotProgress();
  };

  VideoScroll.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
    this.syncAutoplayClass();
  };

  VideoScroll.prototype.pauseAutoplay = function () {
    this.stopAutoplay();
    if (this.autoplayResumeTimer) {
      window.clearTimeout(this.autoplayResumeTimer);
      this.autoplayResumeTimer = null;
    }
  };

  VideoScroll.prototype.scheduleAutoplayResume = function () {
    var self = this;
    if (!this.autoplayEnabled || !this.shouldUseMobileCarousel()) return;

    if (this.autoplayResumeTimer) {
      window.clearTimeout(this.autoplayResumeTimer);
    }

    this.autoplayResumeTimer = window.setTimeout(function () {
      self.autoplayResumeTimer = null;
      self.startAutoplay();
    }, this.autoplayIntervalMs);
  };

  VideoScroll.prototype.syncAutoplay = function () {
    if (this.shouldUseMobileCarousel() && this.autoplayEnabled && this.slides.length > 1) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
  };

  VideoScroll.prototype.onMobilePrev = function () {
    this.pauseAutoplay();
    this.goToMobile(this.index - 1, { loop: true });
    this.scheduleAutoplayResume();
  };

  VideoScroll.prototype.onMobileNext = function () {
    this.pauseAutoplay();
    this.goToMobile(this.index + 1, { loop: true });
    this.scheduleAutoplayResume();
  };

  VideoScroll.prototype.onMobileDotClick = function (event) {
    var dot = event.currentTarget;
    var index = parseInt(dot.getAttribute('data-video-scroll-dot'), 10);
    if (isNaN(index)) return;

    this.pauseAutoplay();
    this.goToMobile(index);
    this.scheduleAutoplayResume();
  };

  VideoScroll.prototype.goTo = function (nextIndex, options) {
    if (this.shouldUseMobileCarousel()) {
      this.goToMobile(nextIndex, options);
      return Promise.resolve();
    }

    var self = this;
    options = options || {};
    var instant = !!options.instant;
    var syncScroll = !!options.syncScroll;
    var max = this.slides.length - 1;

    if (nextIndex < 0 || nextIndex > max || nextIndex === this.index) {
      return Promise.resolve();
    }

    var current = this.slides[this.index];
    var next = this.slides[nextIndex];
    if (!next) return Promise.resolve();

    if (this.transitioning) {
      this.resetAllSlideStates();
      if (current) current.classList.add('is-active');
      this.transitioning = false;
      this.root.setAttribute('data-transitioning', 'false');
    }

    var mediaDir = nextIndex > this.index ? 'next' : 'prev';
    this.transitioning = true;
    this.root.setAttribute('data-transitioning', 'true');
    this.root.setAttribute('data-media-dir', mediaDir);

    if (syncScroll) {
      this.scrollToIndex(nextIndex, instant ? 'auto' : 'smooth');
    }

    var shouldAnimate = !prefersReducedMotion() && !instant;

    if (!shouldAnimate) {
      this.resetSlideState(current);
      this.resetSlideState(next);
      next.classList.add('is-active');
      this.index = nextIndex;
      this.syncVideos(this.index);
      this.finishTransition();
      return Promise.resolve();
    }

    next.classList.remove('is-active', 'is-leaving', 'is-entering');
    next.classList.add('is-incoming');

    if (current && current !== next) {
      void current.offsetWidth;
    }
    void next.offsetWidth;

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (current && current !== next) {
          current.classList.add('is-leaving');
        }
        next.classList.add('is-entering');
      });
    });

    return this.waitForCrossfade(current, next).then(function () {
      if (current && current !== next) {
        current.classList.remove('is-active', 'is-leaving');
      }
      next.classList.remove('is-incoming', 'is-entering');
      next.classList.add('is-active');
      self.index = nextIndex;
      self.syncVideos(self.index);
      self.finishTransition();
    });
  };

  VideoScroll.prototype.finishTransition = function () {
    this.transitioning = false;
    this.root.setAttribute('data-transitioning', 'false');
  };

  VideoScroll.prototype.isStickyStuck = function () {
    if (this.shouldUseMobileCarousel()) return false;
    if (!this.track || !this.sticky) return false;
    var headerOffset = this.getHeaderOffset();
    var stickyTop = this.sticky.getBoundingClientRect().top;
    var trackRect = this.track.getBoundingClientRect();
    var atPinTop = Math.abs(stickyTop - headerOffset) <= 3;
    var trackCoversPin =
      trackRect.top <= headerOffset + 3 && trackRect.bottom >= headerOffset + 24;
    return atPinTop && trackCoversPin;
  };

  VideoScroll.prototype.isSectionInPinRange = function () {
    return this.isStickyStuck();
  };

  VideoScroll.prototype.isTrackNearViewport = function () {
    if (this.shouldUseMobileCarousel()) return false;
    if (!this.track) return false;
    var headerOffset = this.getHeaderOffset();
    var trackRect = this.track.getBoundingClientRect();
    return trackRect.bottom > headerOffset && trackRect.top < window.innerHeight;
  };

  VideoScroll.prototype.isWheelCaptureActive = function () {
    if (this.shouldUseMobileCarousel()) return false;
    return this.isStickyStuck() && this.slides.length > 1;
  };

  VideoScroll.prototype.onWheel = function (event) {
    if (!this.isWheelCaptureActive()) return;

    var delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 16;
    if (event.deltaMode === 2) delta *= window.innerHeight;
    if (Math.abs(delta) < WHEEL_THRESHOLD) return;

    var dir = delta > 0 ? 1 : -1;
    var nextIndex = this.index + dir;
    var canSwitch = nextIndex >= 0 && nextIndex <= this.slides.length - 1;
    var now = Date.now();

    if (now < this.wheelLockedUntil) {
      if (canSwitch) event.preventDefault();
      return;
    }

    if (!canSwitch) return;

    event.preventDefault();
    this.wheelLockedUntil = now + this.wheelCooldownMs;
    this.goTo(nextIndex, { syncScroll: true });
  };

  VideoScroll.prototype.onTouchStart = function (event) {
    if (!event.touches || !event.touches.length) return;

    if (this.shouldUseMobileCarousel()) {
      if (event.touches.length !== 1) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchDeltaX = 0;
      this.isSwiping = false;
      this.pauseAutoplay();
      return;
    }

    this.touchActive = true;
    this.touchStartY = event.touches[0].clientY;
  };

  VideoScroll.prototype.onTouchMove = function (event) {
    if (!event.touches || !event.touches.length) return;

    if (this.shouldUseMobileCarousel()) {
      if (event.touches.length !== 1) return;

      var dx = event.touches[0].clientX - this.touchStartX;
      var dy = event.touches[0].clientY - this.touchStartY;

      if (!this.isSwiping && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        this.isSwiping = true;
      }

      if (this.isSwiping) {
        this.touchDeltaX = dx;
        event.preventDefault();
      }
      return;
    }

    if (!this.touchActive) return;
    if (!this.isWheelCaptureActive()) return;

    var currentY = event.touches[0].clientY;
    var delta = this.touchStartY - currentY;
    var dir = delta > 0 ? 1 : delta < 0 ? -1 : 0;
    var nextIndex = this.index + dir;
    var canSwitch = dir && nextIndex >= 0 && nextIndex <= this.slides.length - 1;
    var now = Date.now();

    if (now < this.wheelLockedUntil) {
      if (canSwitch) event.preventDefault();
      return;
    }

    if (!dir || Math.abs(delta) < TOUCH_THRESHOLD) return;
    if (!canSwitch) return;

    event.preventDefault();
    this.touchActive = false;
    this.wheelLockedUntil = now + this.wheelCooldownMs;
    this.goTo(nextIndex, { syncScroll: true });
  };

  VideoScroll.prototype.onTouchEnd = function () {
    if (this.shouldUseMobileCarousel()) {
      if (this.isSwiping) {
        if (this.touchDeltaX <= -SWIPE_THRESHOLD) {
          this.goToMobile(this.index + 1, { loop: true });
        } else if (this.touchDeltaX >= SWIPE_THRESHOLD) {
          this.goToMobile(this.index - 1, { loop: true });
        }
      }

      this.isSwiping = false;
      this.scheduleAutoplayResume();
      return;
    }

    this.touchActive = false;
  };

  VideoScroll.prototype.onScroll = function () {
    if (this.shouldUseMobileCarousel()) return;
    if (!this.track) return;

    var inRange = this.isSectionInPinRange();
    this.pinned = inRange;
    this.root.classList.toggle('is-pinned', inRange);

    if (this.slides.length < 2) return;
    if (this.wheelAnimating) return;

    if (inRange || this.isTrackNearViewport()) {
      this.syncIndexFromScroll({ instant: true });
    }
  };

  VideoScroll.prototype.onVisibilityChange = function () {
    if (document.hidden) {
      this.pauseAutoplay();
      return;
    }
    this.syncAutoplay();
  };

  VideoScroll.prototype.onModeChange = function () {
    this.pauseAutoplay();
    this.clearMobileAnimLock();
    this.transitioning = false;
    this.wheelAnimating = false;
    this.wheelLockedUntil = 0;
    this.root.setAttribute('data-transitioning', 'false');
    this.root.classList.remove('is-pinned');

    if (this.shouldUseMobileCarousel()) {
      this.resetAllSlideStates();
      this.slides[this.index].classList.add('is-active');
      if (this.sticky) this.sticky.style.top = '';
    } else {
      this.teardownMobileLoop();
      this.syncHeaderOffset(true);
    }

    this.measureLayout();
    this.syncVideos(this.index);

    if (this.shouldUseMobileCarousel()) {
      this.goToMobile(this.index, { instant: true, restartProgress: false, force: true });
      this.syncAutoplay();
    }
  };

  VideoScroll.prototype.onResize = function () {
    this.syncHeaderOffset(true);
    this.measureLayout();
    if (this.shouldUseMobileCarousel()) {
      this.goToMobile(this.index, { instant: true, restartProgress: false, force: true });
    }
    this.syncVideos(this.index);
  };

  VideoScroll.prototype.bind = function () {
    var self = this;
    var touchTarget = this.viewport || this.root;

    this.bound.onWheel = function (event) {
      self.onWheel(event);
    };
    this.bound.onTouchStart = function (event) {
      self.onTouchStart(event);
    };
    this.bound.onTouchMove = function (event) {
      self.onTouchMove(event);
    };
    this.bound.onTouchEnd = function () {
      self.onTouchEnd();
    };
    this.bound.onScroll = function () {
      self.onScroll();
    };
    this.bound.onResize = function () {
      self.onResize();
    };
    this.bound.onModeChange = function () {
      self.onModeChange();
    };
    this.bound.onVisibilityChange = function () {
      self.onVisibilityChange();
    };
    this.bound.onMobilePrev = function () {
      self.onMobilePrev();
    };
    this.bound.onMobileNext = function () {
      self.onMobileNext();
    };
    this.bound.onMobileDotClick = function (event) {
      self.onMobileDotClick(event);
    };

    window.addEventListener('wheel', this.bound.onWheel, { passive: false });
    touchTarget.addEventListener('touchstart', this.bound.onTouchStart, { passive: true });
    touchTarget.addEventListener('touchmove', this.bound.onTouchMove, { passive: false });
    touchTarget.addEventListener('touchend', this.bound.onTouchEnd, { passive: true });
    touchTarget.addEventListener('touchcancel', this.bound.onTouchEnd, { passive: true });
    window.addEventListener('scroll', this.bound.onScroll, { passive: true });
    window.addEventListener('resize', this.bound.onResize);
    document.addEventListener('visibilitychange', this.bound.onVisibilityChange);

    if (this.mobileMediaQuery) {
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.bound.onModeChange);
      } else if (this.mobileMediaQuery.addListener) {
        this.mobileMediaQuery.addListener(this.bound.onModeChange);
      }
    }

    if (this.prevBtn) this.prevBtn.addEventListener('click', this.bound.onMobilePrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.bound.onMobileNext);
    for (var d = 0; d < this.dots.length; d += 1) {
      this.dots[d].addEventListener('click', this.bound.onMobileDotClick);
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(function () {
        if (self.shouldUseMobileCarousel()) {
          if (self.mobileAnimating) return;
          self.setupMobileLoop();
          self.setMobileSlideWidths();
          self.setMobileStageOffset(self.getMobileTrackIndex(self.index));
        } else {
          self.updateTrackHeight();
        }
      });

      if (this.sticky) this.resizeObserver.observe(this.sticky);
      if (this.viewport) this.resizeObserver.observe(this.viewport);

      var header = document.getElementById('shopify-section-header');
      if (header) {
        this.headerObserver = new ResizeObserver(function () {
          self.syncHeaderOffset(false);
        });
        this.headerObserver.observe(header);
      }
    }
  };

  VideoScroll.prototype.destroy = function () {
    var touchTarget = this.viewport || this.root;

    window.removeEventListener('wheel', this.bound.onWheel);
    touchTarget.removeEventListener('touchstart', this.bound.onTouchStart);
    touchTarget.removeEventListener('touchmove', this.bound.onTouchMove);
    touchTarget.removeEventListener('touchend', this.bound.onTouchEnd);
    touchTarget.removeEventListener('touchcancel', this.bound.onTouchEnd);
    window.removeEventListener('scroll', this.bound.onScroll);
    window.removeEventListener('resize', this.bound.onResize);
    document.removeEventListener('visibilitychange', this.bound.onVisibilityChange);

    if (this.bound.onWindowLoad) {
      window.removeEventListener('load', this.bound.onWindowLoad);
    }

    if (this.mobileMediaQuery) {
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this.bound.onModeChange);
      } else if (this.mobileMediaQuery.removeListener) {
        this.mobileMediaQuery.removeListener(this.bound.onModeChange);
      }
    }

    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.bound.onMobilePrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.bound.onMobileNext);
    for (var d = 0; d < this.dots.length; d += 1) {
      this.dots[d].removeEventListener('click', this.bound.onMobileDotClick);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }

    if (this.wheelAnimTimer) {
      window.clearTimeout(this.wheelAnimTimer);
      this.wheelAnimTimer = null;
    }

    this.pauseAutoplay();
    this.clearMobileAnimLock();
    this.teardownMobileLoop();

    if (this.headerRetryTimers && this.headerRetryTimers.length) {
      this.headerRetryTimers.forEach(function (timer) {
        window.clearTimeout(timer);
      });
      this.headerRetryTimers = [];
    }

    this.clearMobileSlideStyles();
    if (this.track) this.track.style.height = '';
    if (this.stage) {
      this.stage.style.transform = '';
      this.stage.style.width = '';
      this.stage.style.transition = '';
    }
    if (this.sticky) this.sticky.style.top = '';

    this.pauseAll();
    this.transitioning = false;
    this.wheelAnimating = false;
    this.wheelLockedUntil = 0;
    this.root.setAttribute('data-transitioning', 'false');
    this.root.classList.remove('is-pinned', 'is-autoplay-active', 'is-vs-ready');
  };

  VideoScroll.prototype.selectIndex = function (index, instant) {
    var max = this.slides.length - 1;
    var next = Math.max(0, Math.min(max, index));
    this.transitioning = false;
    this.wheelAnimating = false;
    this.wheelLockedUntil = 0;

    if (this.shouldUseMobileCarousel()) {
      this.pauseAutoplay();
      this.goToMobile(next, { instant: !!instant, force: true, restartProgress: false });
      this.scheduleAutoplayResume();
      return;
    }

    this.goTo(next, { instant: !!instant, syncScroll: true });
  };

  function initRoot(root) {
    if (!root) return;
    if (root._videoScroll) {
      root._videoScroll.destroy();
      root._videoScroll = null;
    }
    root._videoScroll = new VideoScroll(root);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) {
      initRoot(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var node = event.target.querySelector(SELECTOR);
    if (node && node._videoScroll) {
      node._videoScroll.destroy();
      node._videoScroll = null;
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest(SELECTOR) || event.target.querySelector(SELECTOR);
    if (!root || !root._videoScroll) return;
    var slide =
      event.target.closest('[data-video-scroll-slide]') ||
      event.target.querySelector('[data-video-scroll-slide]');
    if (!slide) return;
    var index = parseInt(slide.getAttribute('data-index'), 10);
    if (isNaN(index)) return;
    root._videoScroll.selectIndex(index, true);
  });
})();
